import { matchPath } from 'react-router-dom';

import { appRoutePath, appRoutePatterns } from './appRoutePatterns';
import { parseOperationsPlanDraftHandoff } from './operationsPlanDraftHandoff';
import {
  canonicalInternalPath,
  INTERNAL_APP_ORIGIN,
  normalizeInternalAppReturnPath,
} from '@/types/internalNavigation';
import { decodePlanKey } from '@/utils/planKey';

export const OPERATIONS_PLANNING_STAGES = [
  'decision',
  'basis',
  'futures',
  'plan',
  'confirm',
] as const;

export type OperationsPlanningStage = (typeof OPERATIONS_PLANNING_STAGES)[number];
export type OperationsPlanningIntent = 'value' | 'no-clearcut';

export interface OperationsPlanningRouteState {
  readonly stage: OperationsPlanningStage;
  readonly stageWasValid: boolean;
  readonly planId: string | null;
  readonly planIdWasValid: boolean;
}

export interface OperationsPlanningNewPlanResolution {
  readonly intent: OperationsPlanningIntent;
  readonly returnTo: string;
}

function uniqueQueryValue(params: URLSearchParams, key: string): string | null {
  const values = params.getAll(key);
  return values.length === 1 ? (values[0] ?? null) : null;
}

export function isOperationsPlanningStage(value: string | null): value is OperationsPlanningStage {
  return OPERATIONS_PLANNING_STAGES.some((stage) => stage === value);
}

export function safeOperationsPlanId(value: string | null): string | null {
  if (
    value === null ||
    value.length === 0 ||
    value.length > 512 ||
    value !== value.trim() ||
    !/^[A-Za-z0-9._:|-]+$/.test(value)
  ) {
    return null;
  }
  return value;
}

export function isOperationsPlanningIntent(
  value: string | null,
): value is OperationsPlanningIntent {
  return value === 'value' || value === 'no-clearcut';
}

export function operationsPlanningPath(propertyId: string): string {
  return appRoutePath(appRoutePatterns.operationsPlanning, { propertyId });
}

export function readOperationsPlanningRouteState(search: string): OperationsPlanningRouteState {
  const params = new URLSearchParams(search);
  const stageValues = params.getAll('stage');
  const planIdValues = params.getAll('plan_id');
  const rawStage = uniqueQueryValue(params, 'stage');
  const rawPlanId = uniqueQueryValue(params, 'plan_id');
  return {
    stage: isOperationsPlanningStage(rawStage) ? rawStage : 'decision',
    stageWasValid:
      stageValues.length === 0 || (stageValues.length === 1 && isOperationsPlanningStage(rawStage)),
    planId: safeOperationsPlanId(rawPlanId),
    planIdWasValid:
      planIdValues.length === 0 ||
      (planIdValues.length === 1 && safeOperationsPlanId(rawPlanId) !== null),
  };
}

export function canonicalOperationsPlannerReturn(args: {
  propertyId: string;
  stage?: OperationsPlanningStage;
  planId?: string | null;
}): string {
  const target = new URL(operationsPlanningPath(args.propertyId), INTERNAL_APP_ORIGIN);
  target.searchParams.set('stage', args.stage ?? 'decision');
  const planId = safeOperationsPlanId(args.planId ?? null);
  if (planId) target.searchParams.set('plan_id', planId);
  return canonicalInternalPath(target);
}

export function buildOperationsPlanningNewPlanPath(args: {
  propertyId: string;
  intent: OperationsPlanningIntent;
  returnTo: string;
}): string {
  const target = new URL(
    `${operationsPlanningPath(args.propertyId)}/new-plan`,
    INTERNAL_APP_ORIGIN,
  );
  target.searchParams.set('intent', args.intent);
  target.searchParams.set('return_to', args.returnTo);
  return canonicalInternalPath(target);
}

export function resolveOperationsPlanningNewPlan(
  search: string,
  propertyId: string,
): OperationsPlanningNewPlanResolution | null {
  const params = new URLSearchParams(search);
  const intent = uniqueQueryValue(params, 'intent');
  const returnTo = normalizeInternalAppReturnPath(uniqueQueryValue(params, 'return_to'));
  if (!isOperationsPlanningIntent(intent) || !returnTo) return null;
  const parsed = new URL(returnTo, INTERNAL_APP_ORIGIN);
  if (parsed.pathname !== operationsPlanningPath(propertyId)) return null;
  const route = readOperationsPlanningRouteState(parsed.search);
  if (!route.stageWasValid || !route.planIdWasValid) return null;
  const canonical = canonicalOperationsPlannerReturn({
    propertyId,
    stage: route.stage,
    planId: route.planId,
  });
  return canonical === returnTo ? { intent, returnTo } : null;
}

/**
 * The intent the planner's generic "create a simulated plan" action opens the
 * new-plan flow with. The flow lets the owner change every preset, so a
 * new-plan URL that names no intent opens with this one.
 */
export const OPERATIONS_PLANNING_GENERIC_CREATE_INTENT: OperationsPlanningIntent = 'value';

/**
 * The canonical new-plan URL for any `/new-plan` location, so a link or a
 * bookmark that omits `intent` or `return_to` still opens the flow instead of
 * leaving the page. A valid intent and a same-property planner return are
 * kept; anything else is replaced by the generic create intent and the
 * planner's Decision stage.
 */
export function canonicalOperationsPlanningNewPlanPath(search: string, propertyId: string): string {
  const params = new URLSearchParams(search);
  const rawIntent = uniqueQueryValue(params, 'intent');
  const intent = isOperationsPlanningIntent(rawIntent)
    ? rawIntent
    : OPERATIONS_PLANNING_GENERIC_CREATE_INTENT;
  const rawReturn = normalizeInternalAppReturnPath(uniqueQueryValue(params, 'return_to'));
  const parsedReturn = rawReturn ? new URL(rawReturn, INTERNAL_APP_ORIGIN) : null;
  const route =
    parsedReturn && parsedReturn.pathname === operationsPlanningPath(propertyId)
      ? readOperationsPlanningRouteState(parsedReturn.search)
      : null;
  return buildOperationsPlanningNewPlanPath({
    propertyId,
    intent,
    returnTo: canonicalOperationsPlannerReturn({
      propertyId,
      stage: route?.stage ?? 'decision',
      planId: route?.planId ?? null,
    }),
  });
}

export function isOperationsPlanningNewPlanTarget(target: string, propertyId: string): boolean {
  const safeTarget = normalizeInternalAppReturnPath(target);
  if (!safeTarget) return false;
  const url = new URL(safeTarget, INTERNAL_APP_ORIGIN);
  return (
    url.pathname === `${operationsPlanningPath(propertyId)}/new-plan` &&
    resolveOperationsPlanningNewPlan(url.search, propertyId) !== null
  );
}

function samePlan(left: string | null, right: string | null): boolean {
  if (left === right) return true;
  if (left === null || right === null) return false;
  const leftPlan = decodePlanKey(left);
  const rightPlan = decodePlanKey(right);
  if (leftPlan.id !== rightPlan.id) return false;
  return (
    (leftPlan.type === null && rightPlan.type === 'optimized') ||
    (rightPlan.type === null && leftPlan.type === 'optimized')
  );
}

export function isDraftRetainingOperationsPlanningNavigation(args: {
  propertyId: string;
  planId: string | null;
  target: string;
}): boolean {
  const safeTarget = normalizeInternalAppReturnPath(args.target);
  if (!safeTarget) return false;
  const url = new URL(safeTarget, INTERNAL_APP_ORIGIN);
  if (url.pathname === operationsPlanningPath(args.propertyId)) {
    const route = readOperationsPlanningRouteState(url.search);
    return (
      route.stageWasValid &&
      route.planIdWasValid &&
      samePlan(route.planId, safeOperationsPlanId(args.planId))
    );
  }
  const standMatch = matchPath(
    { path: appRoutePatterns.standDetail, end: true },
    url.pathname,
  )?.params;
  if (standMatch?.propertyId !== args.propertyId || !standMatch.standId) return false;
  const handoff = parseOperationsPlanDraftHandoff(url.search, {
    propertyId: args.propertyId,
    standId: standMatch.standId,
  });
  return handoff !== null && samePlan(handoff.planId, safeOperationsPlanId(args.planId));
}
